/**
 * settings.ts —— 应用设置（localStorage 持久化）
 *
 * 存储键与设计文档 4.6 一致：
 *   lx.settings  →  { theme, fontScale, locale }
 *   lx.ai        →  { baseUrl, apiKey, model, temperature }
 *   lx.asr       →  { apiKey, resourceId, proxyUrl, language }
 *   lx.ui        →  { lastTab }
 */

import type { AppSettings, AISettings, ASRSettings, ThemeMode } from '@/types/models';
import { eventBus } from '@/core/event-bus';

const KEY_SETTINGS = 'lx.settings';
const KEY_AI = 'lx.ai';
const KEY_ASR = 'lx.asr';

/* ============================================================
   默认值
   ============================================================ */

export const DEFAULT_SETTINGS: AppSettings = {
  theme: 'auto',
  fontScale: 1,
  locale: 'zh-CN',
  lastTab: 'home',
  debugMode: false,
};

export const DEFAULT_AI: AISettings = {
  baseUrl: '',
  apiKey: '',
  model: '',
  temperature: 0.3,
};

export const DEFAULT_ASR: ASRSettings = {
  apiKey: '',
  // 一句话识别（单向流式）的资源 ID
  resourceId: 'volc.seedasr.sauc.duration',
  proxyUrl: 'ws://localhost:8788',
  language: 'zh-CN',
};

/* ============================================================
   读写工具
   ============================================================ */

function read<T extends object>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { ...fallback };
    // 与默认值合并：新增字段时老用户不会拿到 undefined
    return { ...fallback, ...(JSON.parse(raw) as Partial<T>) };
  } catch {
    // 存储损坏时回退到默认值，而不是让应用崩掉
    return { ...fallback };
  }
}

function write<T extends object>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error(`[settings] 写入 ${key} 失败：`, err);
  }
}

/* ============================================================
   对外接口
   ------------------------------------------------------------
   ⚠️ 广播必须由 Repo 层负责，而不是由设置页 UI 负责。

   曾经的写法是「设置页调完 setAI() 后自己 emit('ai:config-changed')」，
   这在分层上是倒置的：只要换一条写入路径（例如 AI 用 setting.set
   指令自己改配置、或将来加一个导入配置的功能），广播就漏了，
   输入栏会永远停在「还没配置 AI 服务」的引导态。

   按项目铁律「操作 → Repo 写库 → 广播事件 → 组件订阅更新」，
   广播是 Repo 的职责：谁写库，谁广播。这样调用方不必记得补一发。
   ============================================================ */

export const settingsRepo = {
  getApp: (): AppSettings => read(KEY_SETTINGS, DEFAULT_SETTINGS),
  setApp: (patch: Partial<AppSettings>): AppSettings => {
    const prev = settingsRepo.getApp();
    const next = { ...prev, ...patch };
    write(KEY_SETTINGS, next);
    // setApp 会被 lastTab 切页这种高频路径调用，无差别广播太吵。
    // 但 debugMode 会改变输入栏形态（IL 按钮出/收），必须通知 ——
    // 所以只在它真正翻转时才发。
    if (prev.debugMode !== next.debugMode) {
      eventBus.emit('ai:config-changed', {});
    }
    return next;
  },

  getAI: (): AISettings => read(KEY_AI, DEFAULT_AI),
  setAI: (patch: Partial<AISettings>): AISettings => {
    const next = { ...settingsRepo.getAI(), ...patch };
    write(KEY_AI, next);
    // 输入栏据此重渲染：是否显示「去设置」引导、是否出现语音/IL 按钮
    eventBus.emit('ai:config-changed', {});
    return next;
  },

  getASR: (): ASRSettings => read(KEY_ASR, DEFAULT_ASR),
  setASR: (patch: Partial<ASRSettings>): ASRSettings => {
    const next = { ...settingsRepo.getASR(), ...patch };
    write(KEY_ASR, next);
    // 语音可用性依赖 ASR 配置，同样需要让输入栏重新判断麦克风是否出现
    eventBus.emit('ai:config-changed', {});
    return next;
  },

  /** AI 是否已配置（用于输入栏的引导提示） */
  isAIReady: (): boolean => {
    const ai = settingsRepo.getAI();
    return Boolean(ai.baseUrl && ai.apiKey && ai.model);
  },

  /** 清空全部本地设置 */
  clearAll(): void {
    [KEY_SETTINGS, KEY_AI, KEY_ASR].forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch {
        /* 忽略 */
      }
    });
  },
};

/* ============================================================
   字号缩放
   ------------------------------------------------------------
   --fs-base 是唯一基准，改它一处，全项目字号按 calc() 派生同步变化。
   这是 variables 化的核心收益，也是 design.md 明确要求的。
   ============================================================ */

export const FONT_SCALE_PRESETS = [
  { label: '小', value: 0.875 },
  { label: '标准', value: 1 },
  { label: '大', value: 1.125 },
  { label: '特大', value: 1.25 },
] as const;

export function applyFontScale(scale: number): void {
  const base = 16 * scale;
  document.documentElement.style.setProperty('--fs-base', `${base}px`);
}

/* ============================================================
   主题
   ============================================================ */

let systemThemeQuery: MediaQueryList | null = null;

/** 把主题模式解析成实际生效的 light / dark */
export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'light' || mode === 'dark') return mode;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** 应用主题到 DOM，并同步移动端浏览器地址栏配色 */
export function applyTheme(mode: ThemeMode): 'light' | 'dark' {
  const resolved = resolveTheme(mode);
  document.documentElement.dataset.theme = resolved;

  // 同步 <meta name="theme-color">，让移动端地址栏跟随
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const bg = getComputedStyle(document.documentElement)
      .getPropertyValue('--c-bg')
      .trim();
    if (bg) meta.setAttribute('content', bg);
  }
  return resolved;
}

/**
 * 启动主题跟随。auto 模式下监听系统配色变化并实时切换。
 * 返回取消函数。
 */
export function watchSystemTheme(onChange: () => void): () => void {
  if (systemThemeQuery) return () => {};
  systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => onChange();
  systemThemeQuery.addEventListener('change', handler);
  return () => {
    systemThemeQuery?.removeEventListener('change', handler);
    systemThemeQuery = null;
  };
}
